<template>
  <div class="sanvault-home">
    <header class="site-header">
      <div class="site-container header-inner">
        <a class="brand" href="#solucao" aria-label="Sanvault - início">
          <img src="../../assets/images/home/favicon.png" width="42" height="42" alt="">
          <span>Sanvault</span>
        </a>

        <button
          class="menu-toggle"
          type="button"
          :aria-expanded="isMenuActive"
          aria-controls="main-navigation"
          aria-label="Abrir menu de navegação"
          @click="toggleMenu"
        >
          <i :class="isMenuActive ? 'ph ph-x' : 'ph ph-list'" aria-hidden="true"></i>
        </button>

        <nav
          id="main-navigation"
          :class="['main-navigation', { 'is-open': isMenuActive }]"
          aria-label="Navegação principal"
        >
          <a href="#solucao" @click="closeMenu">Solução</a>
          <a href="#beneficios" @click="closeMenu">Benefícios</a>
          <a href="#como-funciona" @click="closeMenu">Como funciona</a>
          <a href="#recursos" @click="closeMenu">Recursos</a>
          <a href="#preco" @click="closeMenu">Preço</a>
          <a href="/login" class="login-link">
            <i class="flaticon-user" aria-hidden="true"></i>
            Entrar
          </a>
        </nav>
      </div>
    </header>

    <main>
      <section id="solucao" class="hero-section">
        <div class="hero-grid" aria-hidden="true"></div>
        <div class="site-container hero-layout">
          <div class="hero-copy">
            <span class="eyebrow">
              <span class="eyebrow-dot"></span>
              Governança de gravações + inteligência operacional
            </span>
            <h1>Transforme cada ligação em informação pesquisável e acionável.</h1>
            <p class="hero-description">
              Centralize, transcreva e organize as gravações da sua operação para encontrar
              o que foi dito, acompanhar a qualidade e recuperar evidências em segundos.
            </p>

            <div class="hero-actions">
              <a
                :href="whatsappUrl"
                class="action-button action-button--outline"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i class="ph ph-whatsapp-logo" aria-hidden="true"></i>
                Solicitar demonstração
              </a>
              <router-link to="/register" class="action-button action-button--blue">
                Criar conta grátis
                <i class="ph ph-arrow-right" aria-hidden="true"></i>
              </router-link>
            </div>

            <div class="hero-note">
              <i class="ph ph-check-circle" aria-hidden="true"></i>
              <span>
                Teste com <strong>{{ getGbFree }} GB gratuitos</strong>, sem compromisso.
                Depois, você informa uma forma de pagamento válida.
              </span>
            </div>
          </div>

          <div class="hero-product">
            <div class="product-glow"></div>
            <div class="product-window">
              <div class="window-bar">
                <span></span><span></span><span></span>
                <small>Visão da operação</small>
              </div>
              <img
                src="../../assets/images/home/systemOther.png"
                alt="Tela real da Sanvault com métricas e gravações da operação"
              >
            </div>
            <div class="product-badge product-badge--search">
              <i class="ph ph-magnifying-glass" aria-hidden="true"></i>
              <span><strong>Busca inteligente</strong>Encontre o que foi dito</span>
            </div>
            <div class="product-badge product-badge--transcript">
              <i class="ph ph-waveform" aria-hidden="true"></i>
              <span><strong>Transcrição</strong>Voz transformada em dados</span>
            </div>
          </div>
        </div>
      </section>

      <section class="positioning-section">
        <div class="site-container positioning-layout">
          <div>
            <span class="section-kicker">O que muda na sua operação</span>
            <h2>Gravações deixam de ser arquivos parados e passam a trabalhar pelo seu negócio.</h2>
          </div>
          <p>
            A Sanvault organiza o histórico das chamadas em um ambiente centralizado,
            pesquisável e seguro. Assim, gestores encontram respostas mais rápido,
            acompanham o atendimento e preservam o contexto do que realmente foi falado.
          </p>
        </div>
      </section>

      <section id="beneficios" class="section section--light">
        <div class="site-container">
          <div class="section-heading section-heading--center">
            <span class="section-kicker">Resultados que aparecem no dia a dia</span>
            <h2>Mais clareza para decidir. Menos tempo procurando.</h2>
            <p>Quatro formas de transformar conversas em valor operacional.</p>
          </div>

          <div class="outcome-grid">
            <article class="outcome-card">
              <div class="icon-box"><i class="ph ph-shield-check" aria-hidden="true"></i></div>
              <span class="card-index">01</span>
              <h3>Prova</h3>
              <p>Recupere rapidamente a ligação e o contexto do que foi combinado com o cliente.</p>
            </article>
            <article class="outcome-card">
              <div class="icon-box"><i class="ph ph-lightning" aria-hidden="true"></i></div>
              <span class="card-index">02</span>
              <h3>Produtividade</h3>
              <p>Localize trechos relevantes pelo texto transcrito, sem ouvir chamada por chamada.</p>
            </article>
            <article class="outcome-card">
              <div class="icon-box"><i class="ph ph-chart-line-up" aria-hidden="true"></i></div>
              <span class="card-index">03</span>
              <h3>Qualidade</h3>
              <p>Acompanhe métricas e sinais de sentimento para orientar a supervisão da operação.</p>
            </article>
            <article class="outcome-card">
              <div class="icon-box"><i class="ph ph-magnifying-glass" aria-hidden="true"></i></div>
              <span class="card-index">04</span>
              <h3>Busca inteligente</h3>
              <p>Pesquise por período, origem, arquivo, sentimento ou conteúdo falado.</p>
            </article>
          </div>
        </div>
      </section>

      <section id="como-funciona" class="section section--navy">
        <div class="site-container">
          <div class="section-heading section-heading--inverted">
            <span class="section-kicker section-kicker--cyan">Da ligação à informação</span>
            <h2>Uma camada de inteligência sobre a sua operação de voz.</h2>
            <p>Integre o que você já usa e torne o histórico de chamadas útil para a gestão.</p>
          </div>

          <div class="steps-grid">
            <article class="step-card">
              <span class="step-number">01</span>
              <div class="step-icon"><i class="ph ph-cloud-arrow-up" aria-hidden="true"></i></div>
              <h3>Integre as chamadas</h3>
              <p>Envie gravações pela API ou conecte seu servidor via SFTP para automatizar a entrada dos arquivos.</p>
            </article>
            <article class="step-card">
              <span class="step-number">02</span>
              <div class="step-icon"><i class="ph ph-waveform" aria-hidden="true"></i></div>
              <h3>Enriqueça o conteúdo</h3>
              <p>Converta voz em texto e adicione análise de sentimento para ampliar a leitura das interações.</p>
            </article>
            <article class="step-card">
              <span class="step-number">03</span>
              <div class="step-icon"><i class="ph ph-magnifying-glass" aria-hidden="true"></i></div>
              <h3>Pesquise e acompanhe</h3>
              <p>Encontre conversas por filtros e conteúdo transcrito, ou acompanhe métricas nos painéis da operação.</p>
            </article>
          </div>
        </div>
      </section>

      <section class="section section--cases">
        <div class="site-container">
          <div class="section-heading">
            <span class="section-kicker">Onde a Sanvault gera valor</span>
            <h2>Informação certa para os momentos que mais importam.</h2>
          </div>

          <div class="cases-grid">
            <article class="case-card">
              <span class="case-tag">Agendamento</span>
              <h3>Entenda por que oportunidades não avançam.</h3>
              <p>Localize chamadas, revise abordagens e recupere detalhes informados pelo cliente.</p>
              <i class="ph ph-calendar-check" aria-hidden="true"></i>
            </article>
            <article class="case-card">
              <span class="case-tag">Vendas</span>
              <h3>Encontre objeções e conversas decisivas.</h3>
              <p>Pesquise termos falados e retome negociações com mais contexto.</p>
              <i class="ph ph-trend-up" aria-hidden="true"></i>
            </article>
            <article class="case-card">
              <span class="case-tag">Qualidade</span>
              <h3>Supervisione com mais contexto e agilidade.</h3>
              <p>Use métricas, transcrição e sentimento para direcionar a análise das chamadas.</p>
              <i class="ph ph-headset" aria-hidden="true"></i>
            </article>
            <article class="case-card">
              <span class="case-tag">Evidências</span>
              <h3>Recupere o histórico do que foi falado.</h3>
              <p>Acesse gravação e transcrição em um único fluxo sempre que precisar esclarecer um atendimento.</p>
              <i class="ph ph-file-magnifying-glass" aria-hidden="true"></i>
            </article>
          </div>
        </div>
      </section>

      <section class="section product-proof-section">
        <div class="site-container">
          <div class="section-heading section-heading--center">
            <span class="section-kicker">Produto real, operação visível</span>
            <h2>Do panorama geral ao detalhe de cada chamada.</h2>
            <p>Acompanhe volume, duração, processamento e encontre a interação que precisa analisar.</p>
          </div>

          <div class="proof-stack">
            <div class="proof-window proof-window--dashboard">
              <div class="proof-copy">
                <span>Visão gerencial</span>
                <h3>Métricas organizadas em um só lugar</h3>
                <p>Tenha uma leitura rápida do volume, duração e processamento das gravações da operação.</p>
              </div>
              <div class="proof-image">
                <div class="proof-shot">
                  <img
                    src="../../assets/images/home/systemOther.png"
                    alt="Dashboard real da Sanvault com métricas de gravações"
                    loading="lazy"
                  >
                </div>
              </div>
            </div>
            <div class="proof-window proof-window--search">
              <div class="proof-copy">
                <span>Busca operacional</span>
                <h3>Chegue à conversa certa com poucos filtros</h3>
                <p>Refine por data, identificação, origem, transcrição e sentimento, depois ouça a gravação.</p>
              </div>
              <div class="proof-image">
                <div class="proof-shot">
                  <img
                    src="../../assets/images/home/systemTwo.png"
                    alt="Tela real da Sanvault para pesquisa e reprodução de gravações"
                    loading="lazy"
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="recursos" class="section section--resources">
        <div class="site-container resources-layout">
          <div class="resources-intro">
            <span class="section-kicker">Capacidades da plataforma</span>
            <h2>Recursos para organizar, entender e recuperar cada conversa.</h2>
            <p>
              A Sanvault combina gestão de gravações e inteligência sobre voz sem exigir
              que sua equipe troque toda a infraestrutura de telefonia.
            </p>
            <router-link to="/register" class="text-link">
              Testar a plataforma <i class="ph ph-arrow-up-right" aria-hidden="true"></i>
            </router-link>
          </div>

          <div class="resource-list">
            <div class="resource-item">
              <i class="ph ph-text-align-left" aria-hidden="true"></i>
              <div><h3>Transcrição automática</h3><p>Transforme o áudio em texto para leitura e pesquisa.</p></div>
            </div>
            <div class="resource-item">
              <i class="ph ph-magnifying-glass" aria-hidden="true"></i>
              <div><h3>Busca pelo conteúdo falado</h3><p>Encontre gravações a partir de termos presentes na transcrição.</p></div>
            </div>
            <div class="resource-item">
              <i class="ph ph-smiley" aria-hidden="true"></i>
              <div><h3>Análise de sentimento</h3><p>Use sinais positivos, neutros e negativos para orientar a análise.</p></div>
            </div>
            <div class="resource-item">
              <i class="ph ph-funnel" aria-hidden="true"></i>
              <div><h3>Filtros avançados</h3><p>Pesquise por data, origem, arquivo, sentimento e transcrição.</p></div>
            </div>
            <div class="resource-item">
              <i class="ph ph-chart-bar" aria-hidden="true"></i>
              <div><h3>Métricas e tempo real</h3><p>Acompanhe indicadores gerais, performance e processamento dos uploads.</p></div>
            </div>
            <div class="resource-item">
              <i class="ph ph-users-three" aria-hidden="true"></i>
              <div><h3>Usuários e acessos</h3><p>Organize o uso da plataforma por usuários e permissões.</p></div>
            </div>
            <div class="resource-item">
              <i class="ph ph-plugs-connected" aria-hidden="true"></i>
              <div><h3>API e SFTP</h3><p>Integre a entrada das gravações ao fluxo atual da sua telefonia.</p></div>
            </div>
            <div class="resource-item">
              <i class="ph ph-cloud-check" aria-hidden="true"></i>
              <div><h3>Armazenamento escalável</h3><p>Mantenha o histórico acessível sem ampliar infraestrutura física.</p></div>
            </div>
          </div>
        </div>
      </section>

      <section class="governance-section">
        <div class="site-container governance-layout">
          <div class="governance-symbol" aria-hidden="true">
            <i class="ph ph-shield-check"></i>
          </div>
          <div>
            <span class="section-kicker section-kicker--cyan">Governança começa com organização</span>
            <h2>Gravações centralizadas, acessíveis e sob controle da sua operação.</h2>
            <p>
              Reúna o histórico em nuvem, organize acessos e mantenha as conversas
              disponíveis para consulta quando sua equipe precisar de contexto.
            </p>
          </div>
          <ul>
            <li><i class="ph ph-check" aria-hidden="true"></i> Controle de acesso por usuário</li>
            <li><i class="ph ph-check" aria-hidden="true"></i> Armazenamento seguro em nuvem</li>
            <li><i class="ph ph-check" aria-hidden="true"></i> Acesso rápido às gravações</li>
            <li><i class="ph ph-check" aria-hidden="true"></i> Opção de limitar o espaço armazenado</li>
          </ul>
        </div>
      </section>

      <section id="preco" class="section pricing-section">
        <div class="site-container">
          <div class="section-heading section-heading--center">
            <span class="section-kicker">Infraestrutura transparente</span>
            <h2>Comece pequeno e cresça junto com a sua operação.</h2>
            <p>Você paga pelo espaço utilizado. Transcrição e análise de sentimento são cobradas separadamente.</p>
          </div>

          <div class="pricing-layout">
            <article class="price-card">
              <span class="price-label">Armazenamento em nuvem</span>
              <div class="price">
                <small>R$</small>
                <strong>{{ formattedValue(getServicePrice) }}</strong>
                <span>/ GB</span>
              </div>
              <p>Plataforma, métricas, reprodução, usuários e integrações já incluídos.</p>
              <ul>
                <li><i class="ph ph-check-circle" aria-hidden="true"></i> API ou conexão SFTP</li>
                <li><i class="ph ph-check-circle" aria-hidden="true"></i> Dashboard e performance</li>
                <li><i class="ph ph-check-circle" aria-hidden="true"></i> {{ getGbFree }} GB gratuitos para avaliação</li>
                <li><i class="ph ph-check-circle" aria-hidden="true"></i> Conversão automática de arquivos</li>
              </ul>
              <router-link to="/register" class="action-button action-button--blue action-button--full">
                Criar conta grátis
                <i class="ph ph-arrow-right" aria-hidden="true"></i>
              </router-link>
            </article>

            <div class="calculator-card">
              <div class="calculator-heading">
                <div class="icon-box"><i class="ph ph-calculator" aria-hidden="true"></i></div>
                <div>
                  <span>Simule seu investimento</span>
                  <h3>Quanto sua operação pode utilizar?</h3>
                </div>
              </div>
              <div class="calculator-fields">
                <label>
                  <span>Quantidade de arquivos</span>
                  <input
                    ref="qtdFiles"
                    v-model.number="quantidade"
                    type="number"
                    min="0"
                    inputmode="numeric"
                    placeholder="Ex.: 10.000"
                    @input="calcularValorFinal"
                  >
                </label>
                <label>
                  <span>Tamanho médio por arquivo (MB)</span>
                  <input
                    ref="sizeFiles"
                    v-model.number="tamanhoMedio"
                    type="number"
                    min="0"
                    step="0.01"
                    inputmode="decimal"
                    placeholder="Ex.: 2,5"
                    @input="calcularValorFinal"
                  >
                </label>
              </div>
              <div class="calculator-result" aria-live="polite">
                <span>Estimativa mensal de armazenamento</span>
                <strong>R$ {{ valorFinal }}</strong>
                <small>Funcionalidades de IA são cobradas à parte.</small>
              </div>
              <p class="calculator-note">
                A conversão para GSM pode reduzir o espaço utilizado e tornar o valor final ainda menor.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section class="final-cta-section">
        <div class="site-container final-cta">
          <div>
            <span class="section-kicker section-kicker--cyan">Sua operação já produz os dados</span>
            <h2>Agora faça essas conversas trabalharem a favor do seu negócio.</h2>
          </div>
          <div class="final-actions">
            <a
              :href="whatsappUrl"
              class="action-button action-button--outline-dark"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i class="ph ph-whatsapp-logo" aria-hidden="true"></i>
              Solicitar demonstração
            </a>
            <router-link to="/register" class="action-button action-button--light">
              Criar conta grátis
              <i class="ph ph-arrow-right" aria-hidden="true"></i>
            </router-link>
          </div>
        </div>
      </section>
    </main>

    <footer class="site-footer">
      <div class="site-container footer-layout">
        <div class="footer-brand">
          <a class="brand brand--footer" href="#solucao">
            <img src="../../assets/images/home/favicon.png" width="42" height="42" alt="">
            <span>Sanvault</span>
          </a>
          <p>
            Transformamos gravações de ligações em informação organizada, pesquisável
            e útil para a gestão da sua operação.
          </p>
          <small>CNPJ: 57.916.345/0001-07</small>
        </div>
        <div class="footer-column">
          <h3>Plataforma</h3>
          <a href="#beneficios">Benefícios</a>
          <a href="#como-funciona">Como funciona</a>
          <a href="#recursos">Recursos</a>
          <a href="#preco">Preço</a>
        </div>
        <div class="footer-column">
          <h3>Acesso</h3>
          <router-link to="/login">Entrar</router-link>
          <router-link to="/register">Criar conta</router-link>
          <router-link to="/documentation">Documentação</router-link>
        </div>
        <div class="footer-column">
          <h3>Contato e legal</h3>
          <a href="mailto:contato@sanvault.com.br">contato@sanvault.com.br</a>
          <router-link to="/terms">Termos e condições</router-link>
          <router-link to="/privacy-policy">Política de privacidade</router-link>
          <a
            :href="whatsappUrl"
            target="_blank"
            rel="noopener noreferrer"
          >WhatsApp comercial</a>
        </div>
      </div>
      <div class="site-container footer-bottom">
        <span>© 2026 Sanvault. Todos os direitos reservados.</span>
        <div>
          <a href="https://www.linkedin.com/company/sanvault-tecnologia/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
          <a href="https://www.facebook.com/people/Sanvault-Tecnologia/61579472255039/" target="_blank" rel="noopener noreferrer">Facebook</a>
        </div>
      </div>
    </footer>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { mapActions, mapGetters } from "vuex";

const whatsappMessage =
  "Olá! Quero conhecer a Sanvault e entender como transformar as gravações da minha operação em dados pesquisáveis.";

export default defineComponent({
  name: "HomePage",
  data() {
    return {
      quantidade: 0,
      tamanhoMedio: 0,
      valorFinal: "0,00",
      isMenuActive: false,
      fallbackServicePrice: "1,98",
      whatsappUrl: `https://wa.me/5511915100084?text=${encodeURIComponent(whatsappMessage)}`,
    };
  },
  computed: {
    ...mapGetters(["getServicePrice", "getGbFree"]),
  },
  watch: {
    quantidade() {
      this.calcularValorFinal();
    },
    tamanhoMedio() {
      this.calcularValorFinal();
    },
    getServicePrice() {
      this.calcularValorFinal();
    },
  },
  mounted() {
    this.setServicePrice();
    this.setGbFree();
  },
  methods: {
    ...mapActions(["setServicePrice", "setGbFree"]),
    toggleMenu() {
      this.isMenuActive = !this.isMenuActive;
    },
    closeMenu() {
      this.isMenuActive = false;
    },
    parseServicePrice(value) {
      const priceValue = value === false || value === null || value === undefined
        ? this.fallbackServicePrice
        : value;
      return parseFloat(String(priceValue).replace(",", "."));
    },
    formattedValue(value) {
      const numero = this.parseServicePrice(value);
      return Number.isFinite(numero) ? numero.toFixed(2).replace(".", ",") : "0,00";
    },
    calcularValorFinal() {
      const quantidade = Number(this.quantidade) || 0;
      const tamanhoMedio = Number(this.tamanhoMedio) || 0;
      const preco = this.parseServicePrice(this.getServicePrice) || 0;
      const totalGB = (quantidade * tamanhoMedio) / 1024;
      const totalCurrentPrice = totalGB * preco;
      const valorFinalSemArredondar = Math.floor(totalCurrentPrice * 100) / 100;

      this.valorFinal = new Intl.NumberFormat("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(valorFinalSemArredondar);
    },
  },
});
</script>

<style scoped>
.sanvault-home {
  --navy-950: #061426;
  --navy-900: #0a1c33;
  --navy-800: #102b4e;
  --blue-600: #1d6ff2;
  --blue-500: #3484ff;
  --cyan-500: #20c7d9;
  --cyan-100: #dffbff;
  --ink: #10223b;
  --muted: #607089;
  --line: #dce5f0;
  --surface: #f5f8fc;
  --white: #ffffff;
  color: var(--ink);
  background: var(--white);
  font-family: "Red Hat Display", sans-serif;
  overflow: hidden;
}

.site-container {
  width: min(1180px, calc(100% - 40px));
  margin: 0 auto;
}

.site-header {
  position: sticky;
  top: 0;
  z-index: 1000;
  height: 76px;
  background: rgba(255, 255, 255, 0.94);
  border-bottom: 1px solid rgba(220, 229, 240, 0.85);
  backdrop-filter: blur(18px);
}

.header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
}

.brand {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: var(--navy-950);
  font-size: 22px;
  font-weight: 800;
  text-decoration: none;
  letter-spacing: -0.03em;
}

.brand img {
  border-radius: 11px;
}

.main-navigation {
  display: flex;
  align-items: center;
  gap: 28px;
}

.main-navigation > a {
  color: #33445d;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  transition: color 0.2s ease;
}

.main-navigation > a:hover,
.main-navigation > a:focus-visible {
  color: var(--blue-600);
}

.main-navigation .login-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 17px;
  color: var(--white);
  background: var(--navy-950);
  border-radius: 10px;
}

.main-navigation .login-link:hover,
.main-navigation .login-link:focus-visible {
  color: var(--white);
  background: var(--blue-600);
}

.menu-toggle {
  display: none;
  width: 44px;
  height: 44px;
  padding: 0;
  color: var(--navy-950);
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 10px;
  font-size: 25px;
}

.hero-section {
  position: relative;
  padding: 94px 0 96px;
  background:
    radial-gradient(circle at 78% 34%, rgba(32, 199, 217, 0.13), transparent 30%),
    linear-gradient(180deg, #f8fbff 0%, #ffffff 100%);
}

.hero-grid {
  position: absolute;
  inset: 0;
  opacity: 0.26;
  background-image:
    linear-gradient(rgba(29, 111, 242, 0.08) 1px, transparent 1px),
    linear-gradient(90deg, rgba(29, 111, 242, 0.08) 1px, transparent 1px);
  background-size: 44px 44px;
  mask-image: linear-gradient(to right, transparent 0%, black 62%, transparent 100%);
}

.hero-layout {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 0.93fr) minmax(560px, 1.07fr);
  align-items: center;
  gap: 66px;
}

.eyebrow,
.section-kicker {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  color: var(--blue-600);
  font-size: 13px;
  font-weight: 800;
  line-height: 1.4;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.eyebrow {
  padding: 9px 13px;
  color: #0d5c81;
  background: #e9faff;
  border: 1px solid #c8f3f8;
  border-radius: 999px;
}

.eyebrow-dot {
  width: 7px;
  height: 7px;
  background: var(--cyan-500);
  border-radius: 50%;
  box-shadow: 0 0 0 5px rgba(32, 199, 217, 0.13);
}

.hero-copy h1 {
  max-width: 660px;
  margin: 25px 0 22px;
  color: var(--navy-950);
  font-size: clamp(45px, 4.5vw, 68px);
  font-weight: 800;
  line-height: 1.03;
  letter-spacing: -0.055em;
}

.hero-description {
  max-width: 650px;
  margin: 0;
  color: var(--muted);
  font-size: 19px;
  line-height: 1.65;
}

.hero-actions,
.final-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 32px;
}

.action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 52px;
  padding: 0 22px;
  border: 1px solid transparent;
  border-radius: 11px;
  font-size: 15px;
  font-weight: 800;
  line-height: 1;
  text-decoration: none;
  box-shadow: 0 12px 26px rgba(6, 20, 38, 0.12);
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.action-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 30px rgba(6, 20, 38, 0.18);
}

.action-button--blue {
  color: var(--white);
  background: var(--blue-600);
}

.action-button--blue:hover,
.action-button--blue:focus-visible {
  color: var(--white);
  background: #0c5ddb;
}

.action-button--outline {
  color: var(--navy-950);
  background: var(--white);
  border-color: #cbd8e8;
  box-shadow: 0 8px 20px rgba(19, 48, 84, 0.08);
}

.action-button--outline i {
  color: #159269;
  font-size: 18px;
}

.action-button--outline:hover,
.action-button--outline:focus-visible {
  color: var(--blue-600);
  background: #f5f9ff;
  border-color: #9dbbe5;
  box-shadow: 0 12px 24px rgba(29, 111, 242, 0.13);
}

.action-button--outline-dark {
  color: var(--white);
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.36);
  box-shadow: none;
}

.action-button--outline-dark i {
  color: var(--cyan-500);
  font-size: 18px;
}

.action-button--outline-dark:hover,
.action-button--outline-dark:focus-visible {
  color: var(--white);
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.72);
  box-shadow: 0 12px 24px rgba(3, 16, 32, 0.18);
}

.action-button--light {
  color: var(--navy-950);
  background: var(--white);
}

.action-button--light:hover,
.action-button--light:focus-visible {
  color: var(--navy-950);
  background: #edf5ff;
}

.action-button--full {
  width: 100%;
}

.hero-note {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  max-width: 570px;
  margin-top: 23px;
  color: #607089;
  font-size: 13px;
  line-height: 1.5;
}

.hero-note i {
  flex: 0 0 auto;
  margin-top: 2px;
  color: #159269;
  font-size: 18px;
}

.hero-product {
  position: relative;
  min-width: 0;
  padding: 34px 0 38px;
}

.product-glow {
  position: absolute;
  inset: 10% 2% 0;
  background: linear-gradient(135deg, rgba(29, 111, 242, 0.2), rgba(32, 199, 217, 0.23));
  border-radius: 50%;
  filter: blur(55px);
}

.product-window {
  position: relative;
  overflow: hidden;
  background: var(--white);
  border: 1px solid #dfe8f4;
  border-radius: 18px;
  box-shadow: 0 30px 70px rgba(18, 49, 91, 0.2);
  transform: perspective(1200px) rotateY(-2deg) rotateX(1deg);
}

.window-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 14px;
  background: #f3f6fb;
  border-bottom: 1px solid #e5ebf3;
}

.window-bar span {
  width: 7px;
  height: 7px;
  background: #b9c5d5;
  border-radius: 50%;
}

.window-bar small {
  margin-left: auto;
  color: #75839a;
  font-size: 10px;
  font-weight: 700;
}

.product-window img {
  display: block;
  width: 118%;
  max-width: none;
  margin: -1% 0 -2% -6%;
}

.product-badge {
  position: absolute;
  z-index: 3;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 190px;
  padding: 13px 15px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #dbe6f2;
  border-radius: 13px;
  box-shadow: 0 18px 42px rgba(11, 36, 71, 0.16);
}

.product-badge i {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  width: 38px;
  height: 38px;
  color: var(--blue-600);
  background: #edf4ff;
  border-radius: 10px;
  font-size: 20px;
}

.product-badge span {
  display: flex;
  flex-direction: column;
  color: #6d7b8e;
  font-size: 10px;
}

.product-badge strong {
  margin-bottom: 2px;
  color: var(--navy-950);
  font-size: 12px;
}

.product-badge--search {
  right: -22px;
  bottom: 3px;
}

.product-badge--transcript {
  top: 2px;
  left: -31px;
}

.positioning-section {
  padding: 58px 0;
  background: var(--navy-950);
}

.positioning-layout {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  align-items: center;
  gap: 80px;
}

.positioning-layout h2 {
  max-width: 660px;
  margin: 10px 0 0;
  color: var(--white);
  font-size: clamp(29px, 3vw, 42px);
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: -0.035em;
}

.positioning-layout p {
  margin: 0;
  padding-left: 32px;
  color: #b8c7da;
  border-left: 1px solid rgba(255, 255, 255, 0.16);
  font-size: 16px;
  line-height: 1.75;
}

.section {
  padding: 104px 0;
}

.section--light {
  background: var(--surface);
}

.section--navy {
  position: relative;
  color: var(--white);
  background:
    radial-gradient(circle at 90% 5%, rgba(32, 199, 217, 0.12), transparent 28%),
    var(--navy-950);
}

.section-heading {
  max-width: 720px;
  margin-bottom: 48px;
}

.section-heading--center {
  margin-right: auto;
  margin-left: auto;
  text-align: center;
}

.section-heading h2,
.resources-intro h2 {
  margin: 12px 0 14px;
  color: var(--navy-950);
  font-size: clamp(34px, 3.7vw, 52px);
  font-weight: 800;
  line-height: 1.12;
  letter-spacing: -0.045em;
}

.section-heading p,
.resources-intro > p {
  margin: 0;
  color: var(--muted);
  font-size: 17px;
  line-height: 1.7;
}

.section-heading--inverted h2 {
  color: var(--white);
}

.section-heading--inverted p {
  color: #aebdd1;
}

.section-kicker--cyan {
  color: var(--cyan-500);
}

.outcome-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
}

.outcome-card {
  position: relative;
  min-height: 280px;
  padding: 28px;
  background: var(--white);
  border: 1px solid #e2e9f2;
  border-radius: 18px;
  box-shadow: 0 15px 40px rgba(21, 52, 89, 0.06);
}

.icon-box {
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  color: var(--blue-600);
  background: #eaf2ff;
  border-radius: 13px;
  font-size: 24px;
}

.card-index {
  position: absolute;
  top: 30px;
  right: 28px;
  color: #bdc8d6;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
}

.outcome-card h3 {
  margin: 54px 0 10px;
  color: var(--navy-950);
  font-size: 22px;
  font-weight: 800;
}

.outcome-card p {
  margin: 0;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.7;
}

.steps-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 22px;
}

.step-card {
  position: relative;
  min-height: 320px;
  padding: 32px;
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 18px;
}

.step-card:not(:last-child)::after {
  content: "";
  position: absolute;
  top: 63px;
  right: -23px;
  z-index: 2;
  width: 23px;
  border-top: 1px dashed rgba(32, 199, 217, 0.5);
}

.step-number {
  position: absolute;
  top: 28px;
  right: 29px;
  color: rgba(255, 255, 255, 0.25);
  font-size: 13px;
  font-weight: 800;
}

.step-icon {
  display: grid;
  place-items: center;
  width: 58px;
  height: 58px;
  color: var(--cyan-500);
  background: rgba(32, 199, 217, 0.09);
  border: 1px solid rgba(32, 199, 217, 0.2);
  border-radius: 15px;
  font-size: 28px;
}

.step-card h3 {
  margin: 54px 0 12px;
  color: var(--white);
  font-size: 22px;
  font-weight: 800;
}

.step-card p {
  margin: 0;
  color: #aebdd1;
  font-size: 15px;
  line-height: 1.75;
}

.section--cases {
  background: var(--white);
}

.cases-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;
}

.case-card {
  position: relative;
  min-height: 245px;
  padding: 31px 100px 31px 31px;
  overflow: hidden;
  background: #f7faff;
  border: 1px solid #e0e8f2;
  border-radius: 18px;
}

.case-card::before {
  content: "";
  position: absolute;
  right: -70px;
  bottom: -95px;
  width: 200px;
  height: 200px;
  background: rgba(29, 111, 242, 0.05);
  border-radius: 50%;
}

.case-tag {
  display: inline-block;
  padding: 6px 9px;
  color: var(--blue-600);
  background: #e8f1ff;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.case-card h3 {
  max-width: 410px;
  margin: 26px 0 10px;
  color: var(--navy-950);
  font-size: 23px;
  font-weight: 800;
  line-height: 1.25;
}

.case-card p {
  max-width: 420px;
  margin: 0;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.7;
}

.case-card > i {
  position: absolute;
  right: 29px;
  bottom: 30px;
  color: rgba(29, 111, 242, 0.55);
  font-size: 44px;
}

.product-proof-section {
  background: var(--surface);
}

.proof-stack {
  display: grid;
  gap: 28px;
}

.proof-window {
  display: grid;
  grid-template-columns: minmax(300px, 0.38fr) minmax(0, 0.62fr);
  align-items: stretch;
  overflow: hidden;
  background: var(--white);
  border: 1px solid #dfe7f1;
  border-radius: 24px;
  box-shadow: 0 22px 60px rgba(19, 48, 84, 0.1);
}

.proof-window--search {
  grid-template-columns: minmax(0, 0.62fr) minmax(300px, 0.38fr);
}

.proof-window--search .proof-copy {
  order: 2;
}

.proof-copy {
  align-self: center;
  padding: 52px 48px;
}

.proof-copy > span {
  color: var(--blue-600);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.proof-copy h3 {
  margin: 16px 0 13px;
  color: var(--navy-950);
  font-size: 28px;
  font-weight: 800;
  line-height: 1.2;
}

.proof-copy p {
  margin: 0;
  color: var(--muted);
  font-size: 15px;
  line-height: 1.7;
}

.proof-image {
  display: flex;
  align-items: center;
  overflow: hidden;
  min-height: 460px;
  background: linear-gradient(135deg, #edf3fb, #f8fbff);
}

.proof-shot {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: var(--white);
}

.proof-shot img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.section--resources {
  background: var(--white);
}

.resources-layout {
  display: grid;
  grid-template-columns: 0.4fr 0.6fr;
  gap: 90px;
}

.resources-intro {
  position: sticky;
  top: 116px;
  align-self: start;
}

.text-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 24px;
  color: var(--blue-600);
  font-size: 15px;
  font-weight: 800;
  text-decoration: none;
}

.resource-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.resource-item {
  display: flex;
  gap: 15px;
  min-height: 155px;
  padding: 24px;
  background: #f8faff;
  border: 1px solid #e1e8f1;
  border-radius: 15px;
}

.resource-item > i {
  flex: 0 0 auto;
  color: var(--blue-600);
  font-size: 25px;
}

.resource-item h3 {
  margin: 0 0 8px;
  color: var(--navy-950);
  font-size: 17px;
  font-weight: 800;
}

.resource-item p {
  margin: 0;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.6;
}

.governance-section {
  padding: 72px 0;
  color: var(--white);
  background:
    linear-gradient(115deg, rgba(29, 111, 242, 0.13), transparent 42%),
    var(--navy-950);
}

.governance-layout {
  display: grid;
  grid-template-columns: auto 1fr 0.8fr;
  align-items: center;
  gap: 36px;
}

.governance-symbol {
  display: grid;
  place-items: center;
  width: 86px;
  height: 86px;
  color: var(--cyan-500);
  background: rgba(32, 199, 217, 0.08);
  border: 1px solid rgba(32, 199, 217, 0.2);
  border-radius: 22px;
  font-size: 42px;
}

.governance-layout h2 {
  max-width: 620px;
  margin: 10px 0 12px;
  color: var(--white);
  font-size: clamp(28px, 3vw, 39px);
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: -0.035em;
}

.governance-layout p {
  max-width: 620px;
  margin: 0;
  color: #afbed1;
  font-size: 15px;
  line-height: 1.7;
}

.governance-layout ul {
  display: grid;
  gap: 14px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.governance-layout li {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #d5deea;
  font-size: 14px;
  font-weight: 700;
}

.governance-layout li i {
  color: var(--cyan-500);
}

.pricing-section {
  background: var(--surface);
}

.pricing-layout {
  display: grid;
  grid-template-columns: 0.42fr 0.58fr;
  gap: 26px;
  max-width: 1040px;
  margin: 0 auto;
}

.price-card,
.calculator-card {
  padding: 38px;
  background: var(--white);
  border: 1px solid #dfe7f1;
  border-radius: 20px;
  box-shadow: 0 20px 55px rgba(18, 48, 85, 0.08);
}

.price-card {
  color: var(--white);
  background:
    radial-gradient(circle at 110% -10%, rgba(32, 199, 217, 0.24), transparent 34%),
    var(--navy-950);
  border-color: rgba(255, 255, 255, 0.08);
}

.price-label {
  color: var(--cyan-500);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.price {
  display: flex;
  align-items: baseline;
  gap: 7px;
  margin: 21px 0 13px;
}

.price small {
  font-size: 19px;
  font-weight: 700;
}

.price strong {
  font-size: 57px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.05em;
}

.price > span {
  color: #aabbd0;
  font-size: 15px;
}

.price-card > p {
  min-height: 48px;
  margin: 0;
  color: #aebdd1;
  font-size: 14px;
  line-height: 1.65;
}

.price-card ul {
  display: grid;
  gap: 12px;
  margin: 28px 0 32px;
  padding: 28px 0 0;
  border-top: 1px solid rgba(255, 255, 255, 0.11);
  list-style: none;
}

.price-card li {
  display: flex;
  align-items: center;
  gap: 9px;
  color: #d5deea;
  font-size: 13px;
  font-weight: 700;
}

.price-card li i {
  color: var(--cyan-500);
}

.calculator-heading {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 30px;
}

.calculator-heading span {
  color: var(--blue-600);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.calculator-heading h3 {
  margin: 3px 0 0;
  color: var(--navy-950);
  font-size: 22px;
  font-weight: 800;
}

.calculator-fields {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.calculator-fields label {
  display: grid;
  gap: 8px;
}

.calculator-fields label > span {
  color: #42526a;
  font-size: 13px;
  font-weight: 800;
}

.calculator-fields input {
  width: 100%;
  height: 52px;
  padding: 0 15px;
  color: var(--ink);
  background: #f8faff;
  border: 1px solid #dce5ef;
  border-radius: 10px;
  outline: none;
  font: inherit;
}

.calculator-fields input:focus {
  border-color: var(--blue-600);
  box-shadow: 0 0 0 4px rgba(29, 111, 242, 0.1);
}

.calculator-result {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 24px;
  padding: 26px;
  text-align: center;
  background: #edf5ff;
  border: 1px solid #d7e7fc;
  border-radius: 14px;
}

.calculator-result span {
  color: #5e7088;
  font-size: 12px;
  font-weight: 700;
}

.calculator-result strong {
  margin: 6px 0;
  color: var(--navy-950);
  font-size: 39px;
  font-weight: 800;
  letter-spacing: -0.04em;
}

.calculator-result small,
.calculator-note {
  color: #74849a;
  font-size: 11px;
}

.calculator-note {
  margin: 18px 0 0;
  text-align: center;
  line-height: 1.6;
}

.final-cta-section {
  padding: 75px 0;
  color: var(--white);
  background:
    radial-gradient(circle at 85% 50%, rgba(32, 199, 217, 0.16), transparent 23%),
    linear-gradient(120deg, #0a1c33, #0b2447);
}

.final-cta {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 42px;
}

.final-cta h2 {
  max-width: 690px;
  margin: 11px 0 0;
  color: var(--white);
  font-size: clamp(31px, 3.4vw, 46px);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.04em;
}

.final-actions {
  flex-wrap: nowrap;
  margin-top: 0;
}

.site-footer {
  padding: 72px 0 24px;
  color: #aab8ca;
  background: #05101e;
}

.footer-layout {
  display: grid;
  grid-template-columns: 1.6fr repeat(3, 0.7fr);
  gap: 55px;
}

.brand--footer {
  color: var(--white);
}

.footer-brand p {
  max-width: 390px;
  margin: 22px 0 15px;
  font-size: 14px;
  line-height: 1.75;
}

.footer-brand small {
  color: #718197;
}

.footer-column {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}

.footer-column h3 {
  margin: 4px 0 10px;
  color: var(--white);
  font-size: 14px;
  font-weight: 800;
}

.footer-column a {
  color: #99a9be;
  font-size: 13px;
  text-decoration: none;
}

.footer-column a:hover,
.footer-column a:focus-visible {
  color: var(--cyan-500);
}

.footer-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 60px;
  padding-top: 23px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 12px;
}

.footer-bottom div {
  display: flex;
  gap: 18px;
}

.footer-bottom a {
  color: #99a9be;
  text-decoration: none;
}

.sanvault-home :where(a, button, input):focus-visible {
  outline: 3px solid rgba(32, 199, 217, 0.65);
  outline-offset: 3px;
}

@media (max-width: 1100px) {
  .hero-layout {
    grid-template-columns: minmax(0, 0.88fr) minmax(470px, 1.12fr);
    gap: 38px;
  }

  .hero-copy h1 {
    font-size: 52px;
  }

  .outcome-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .final-cta {
    grid-template-columns: 1fr;
  }

  .final-actions {
    margin-top: 0;
  }
}

@media (max-width: 992px) {
  .site-header {
    height: 70px;
  }

  .menu-toggle {
    display: grid;
    place-items: center;
  }

  .main-navigation {
    position: absolute;
    top: 69px;
    right: 20px;
    left: 20px;
    display: none;
    align-items: stretch;
    gap: 0;
    padding: 14px;
    background: var(--white);
    border: 1px solid var(--line);
    border-radius: 14px;
    box-shadow: 0 20px 45px rgba(7, 27, 54, 0.17);
  }

  .main-navigation.is-open {
    display: grid;
  }

  .main-navigation > a {
    padding: 12px;
  }

  .main-navigation .login-link {
    justify-content: center;
    margin-top: 5px;
  }

  .hero-section {
    padding: 70px 0 80px;
  }

  .hero-layout {
    grid-template-columns: 1fr;
    gap: 45px;
  }

  .hero-copy {
    max-width: 760px;
  }

  .hero-copy h1 {
    max-width: 760px;
    font-size: clamp(44px, 8vw, 66px);
  }

  .hero-product {
    width: min(760px, 95%);
    margin: 0 auto;
  }

  .positioning-layout {
    grid-template-columns: 1fr;
    gap: 28px;
  }

  .positioning-layout p {
    padding: 0;
    border: 0;
  }

  .steps-grid {
    grid-template-columns: 1fr;
  }

  .step-card {
    min-height: 0;
  }

  .step-card:not(:last-child)::after {
    display: none;
  }

  .step-card h3 {
    margin-top: 32px;
  }

  .proof-window,
  .proof-window--search {
    grid-template-columns: 1fr;
  }

  .proof-window--search .proof-copy {
    order: 0;
  }

  .proof-image {
    min-height: 330px;
  }

  .resources-layout {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .resources-intro {
    position: static;
  }

  .governance-layout {
    grid-template-columns: auto 1fr;
  }

  .governance-layout ul {
    grid-column: 1 / -1;
    grid-template-columns: repeat(2, 1fr);
    margin-top: 10px;
  }

  .pricing-layout {
    grid-template-columns: 1fr;
  }

  .footer-layout {
    grid-template-columns: 1.4fr repeat(3, 1fr);
    gap: 35px;
  }
}

@media (max-width: 768px) {
  .site-container {
    width: min(100% - 30px, 1180px);
  }

  .section {
    padding: 78px 0;
  }

  .hero-copy h1 {
    font-size: clamp(41px, 12.5vw, 58px);
  }

  .hero-description {
    font-size: 17px;
  }

  .hero-actions .action-button {
    flex: 1 1 220px;
  }

  .product-badge {
    display: none;
  }

  .hero-product {
    width: 100%;
    padding: 10px 0;
  }

  .product-window {
    transform: none;
  }

  .outcome-grid,
  .cases-grid,
  .resource-list {
    grid-template-columns: 1fr;
  }

  .outcome-card {
    min-height: 240px;
  }

  .proof-copy {
    padding: 30px;
  }

  .proof-image {
    min-height: 260px;
  }

  .governance-layout {
    grid-template-columns: 1fr;
  }

  .governance-layout ul {
    grid-column: auto;
    grid-template-columns: 1fr;
  }

  .calculator-fields {
    grid-template-columns: 1fr;
  }

  .final-actions {
    flex-wrap: wrap;
  }

  .final-actions .action-button {
    flex: 1 1 220px;
  }

  .footer-layout {
    grid-template-columns: repeat(2, 1fr);
  }

  .footer-brand {
    grid-column: 1 / -1;
  }
}

@media (max-width: 520px) {
  .site-container {
    width: min(100% - 24px, 1180px);
  }

  .brand {
    font-size: 20px;
  }

  .eyebrow {
    align-items: flex-start;
    border-radius: 10px;
    font-size: 11px;
  }

  .eyebrow-dot {
    margin-top: 4px;
  }

  .hero-section {
    padding-top: 54px;
  }

  .hero-copy h1 {
    font-size: 42px;
  }

  .hero-actions {
    display: grid;
  }

  .hero-actions .action-button {
    width: 100%;
  }

  .positioning-section {
    padding: 50px 0;
  }

  .section-heading {
    margin-bottom: 36px;
  }

  .section-heading h2,
  .resources-intro h2 {
    font-size: 35px;
  }

  .case-card {
    padding-right: 28px;
  }

  .case-card > i {
    display: none;
  }

  .proof-window {
    min-height: 0;
  }

  .proof-image {
    min-height: 210px;
  }

  .price-card,
  .calculator-card {
    padding: 28px 22px;
  }

  .price strong {
    font-size: 49px;
  }

  .calculator-result strong {
    font-size: 34px;
  }

  .footer-layout {
    grid-template-columns: 1fr;
  }

  .footer-brand {
    grid-column: auto;
  }

  .footer-bottom {
    align-items: flex-start;
    flex-direction: column;
    gap: 14px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .action-button,
  .main-navigation > a {
    transition: none;
  }

  .action-button:hover {
    transform: none;
  }
}
</style>
